import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { XMarkIcon, DocumentArrowDownIcon } from '@heroicons/react/24/outline';
import { pdf } from '@react-pdf/renderer';
import * as XLSX from 'xlsx';
import toast from 'react-hot-toast';
import DriverStatementDocument from '../pdf/DriverStatementDocument';
import { companySettingsService } from '../../services/system/company-settings';

const toMillis = (v) => {
  if (!v) return 0;
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const n = Date.parse(v);
    return Number.isNaN(n) ? 0 : n;
  }
  if (typeof v.toDate === 'function') return v.toDate().getTime();
  if (v.seconds) return v.seconds * 1000;
  return 0;
};

const dayKey = (ms) => {
  if (!ms) return 0;
  const d = new Date(ms);
  return d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
};

const fmtDateOnly = (ms) => {
  if (!ms) return '';
  const d = new Date(ms);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const sanitizeFilename = (s) =>
  (s || 'driver').replace(/[^a-zA-Z0-9_\-\u0600-\u06FF]+/g, '_').slice(0, 60);

const DriverStatementModal = ({
  isOpen,
  onClose,
  driver,
  company,
  tripEntries = [],
  paymentEntries = [],
}) => {
  const { t, i18n } = useTranslation();
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = (() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().slice(0, 10);
  })();

  const [dateFrom, setDateFrom] = useState(monthAgo);
  const [dateTo, setDateTo] = useState(today);
  const [language, setLanguage] = useState(i18n.language?.startsWith('ar') ? 'ar' : 'en');
  const [format, setFormat] = useState('pdf');
  const [entryFilter, setEntryFilter] = useState('all'); // all | trips | payments
  const [generating, setGenerating] = useState(false);
  const [companySettings, setCompanySettings] = useState(null);

  useEffect(() => {
    if (!isOpen || !company?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const s = await companySettingsService.getCompanySettings(company.id);
        if (!cancelled) setCompanySettings(s);
      } catch (err) {
        console.warn('Failed to load company settings:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, company?.id]);

  const filtered = useMemo(() => {
    if (!isOpen) return { entries: [], totals: { totalEarned: 0, totalPaid: 0, balance: 0 } };
    const fromMs = dateFrom ? new Date(dateFrom + 'T00:00:00').getTime() : 0;
    const toMs = dateTo ? new Date(dateTo + 'T23:59:59').getTime() : Number.MAX_SAFE_INTEGER;

    const inRange = (e) => {
      const ms = e.dateMs || 0;
      return ms >= fromMs && ms <= toMs;
    };

    const trips = tripEntries.filter(inRange);
    const payments = paymentEntries.filter(inRange);

    const merged = [...trips, ...payments].sort((a, b) => {
      const dayDiff = dayKey(a.dateMs) - dayKey(b.dateMs);
      if (dayDiff !== 0) return dayDiff;
      return (a.createdMs || 0) - (b.createdMs || 0);
    });

    let running = 0;
    const withBalance = merged.map(e => {
      running += e.type === 'trip' ? (Number(e.amount) || 0) : -(Number(e.amount) || 0);
      return { ...e, balance: running };
    });

    const visible =
      entryFilter === 'trips' ? withBalance.filter(e => e.type === 'trip')
      : entryFilter === 'payments' ? withBalance.filter(e => e.type === 'payment')
      : withBalance;

    const totalEarned = trips.reduce((s, e) => s + (Number(e.amount) || 0), 0);
    const totalPaid = payments.reduce((s, e) => s + (Number(e.amount) || 0), 0);

    return {
      entries: visible,
      totals: { totalEarned, totalPaid, balance: totalEarned - totalPaid },
    };
  }, [isOpen, tripEntries, paymentEntries, dateFrom, dateTo, entryFilter]);

  if (!isOpen) return null;

  const handleGenerate = async () => {
    if (filtered.entries.length === 0) {
      toast.error(t('drivers.statement_no_entries', 'No entries in selected range'));
      return;
    }
    setGenerating(true);
    try {
      const baseName = `${sanitizeFilename(driver?.name)}_statement_${dateFrom}_${dateTo}`;

      if (format === 'pdf') {
        const blob = await pdf(
          <DriverStatementDocument
            driver={{
              name: driver?.name,
              phone: driver?.phone,
              code: driver?.driverCode || driver?.code,
            }}
            company={{
              name: companySettings?.companyName || company?.companyName || company?.name || '',
              address: companySettings?.address || company?.address || '',
              phone: companySettings?.phone || company?.phone || '',
              email: companySettings?.email || company?.email || '',
              website: companySettings?.website || company?.website || '',
              vatId: companySettings?.vatId || companySettings?.vatNumber || '',
              logo: companySettings?.logoBase64 || companySettings?.logoUrl || company?.logoBase64 || company?.logoUrl || null,
            }}
            entries={filtered.entries}
            totals={filtered.totals}
            dateFrom={dateFrom}
            dateTo={dateTo}
            lang={language}
          />
        ).toBlob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${baseName}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } else {
        // Excel
        const isAr = language === 'ar';
        const labels = isAr
          ? {
              statement: 'كشف حساب السائق',
              driver: 'السائق',
              phone: 'الهاتف',
              from: 'من',
              to: 'إلى',
              date: 'التاريخ',
              type: 'النوع',
              description: 'الوصف',
              detail: 'تفاصيل',
              credit: 'دائن (للسائق)',
              debit: 'مدين (مدفوع)',
              balance: 'الرصيد',
              trip: 'رحلة',
              payment: 'دفعة',
              totals: 'الإجمالي',
              totalTrips: 'إجمالي مبلغ الرحلات',
              totalPaid: 'إجمالي المدفوع',
              remaining: 'الرصيد المتبقي',
              currency: 'ر.س',
              pending: 'قيد التحديد',
            }
          : {
              statement: 'Driver Account Statement',
              driver: 'Driver',
              phone: 'Phone',
              from: 'From',
              to: 'To',
              date: 'Date',
              type: 'Type',
              description: 'Description',
              detail: 'Detail',
              credit: 'Credit (Driver)',
              debit: 'Debit (Paid)',
              balance: 'Balance',
              trip: 'Trip',
              payment: 'Payment',
              totals: 'Totals',
              totalTrips: 'Total trip amount',
              totalPaid: 'Total paid',
              remaining: 'Remaining balance',
              currency: 'SAR',
              pending: 'Pending',
            };

        const resolvedCompanyName = companySettings?.companyName || company?.companyName || company?.name || '';
        const aoa = [
          [labels.statement],
          [resolvedCompanyName],
          [],
          [labels.driver, driver?.name || ''],
          [labels.phone, driver?.phone || ''],
          [labels.from, dateFrom, labels.to, dateTo],
          [],
          [labels.date, labels.type, labels.description, labels.detail, labels.credit, labels.debit, labels.balance],
        ];
        filtered.entries.forEach(e => {
          aoa.push([
            fmtDateOnly(e.dateMs),
            e.type === 'trip' ? labels.trip : labels.payment,
            e.description || '',
            e.secondary || '',
            e.type === 'trip'
              ? (e.amount > 0 ? Number(e.amount) : labels.pending)
              : '',
            e.type === 'payment' ? Number(e.amount) : '',
            Number(e.balance.toFixed(2)),
          ]);
        });
        aoa.push([]);
        aoa.push([labels.totalTrips, '', '', '', Number(filtered.totals.totalEarned.toFixed(2))]);
        aoa.push([labels.totalPaid, '', '', '', '', Number(filtered.totals.totalPaid.toFixed(2))]);
        aoa.push([labels.remaining, '', '', '', '', '', Number(filtered.totals.balance.toFixed(2))]);

        const ws = XLSX.utils.aoa_to_sheet(aoa);
        ws['!cols'] = [
          { wch: 12 }, { wch: 10 }, { wch: 32 }, { wch: 30 },
          { wch: 14 }, { wch: 14 }, { wch: 14 },
        ];
        if (isAr) ws['!sheetView'] = [{ RTL: true }];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Statement');
        const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${baseName}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }

      toast.success(t('drivers.statement_generated', 'Statement generated'));
      onClose();
    } catch (err) {
      console.error('Statement generation failed:', err);
      toast.error(`${t('drivers.statement_failed', 'Failed to generate statement')}: ${err?.message || err}`);
    } finally {
      setGenerating(false);
    }
  };

  const setQuickRange = (months) => {
    const to = new Date();
    const from = new Date();
    from.setMonth(from.getMonth() - months);
    setDateFrom(from.toISOString().slice(0, 10));
    setDateTo(to.toISOString().slice(0, 10));
  };

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
          <div className="flex items-center gap-2">
            <DocumentArrowDownIcon className="h-5 w-5 text-blue-600" />
            <h3 className="text-base font-semibold text-gray-900">
              {t('drivers.generate_statement', 'Generate Account Statement')}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={generating}
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {/* Date range */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              {t('drivers.statement_date_range', 'Date range')}
            </label>
            <div className="grid grid-cols-2 gap-2">
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="input-field"
              />
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="input-field"
              />
            </div>
            <div className="flex gap-2 mt-2">
              <button type="button" onClick={() => setQuickRange(1)} className="text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-700">
                {t('drivers.last_30_days', 'Last 30 days')}
              </button>
              <button type="button" onClick={() => setQuickRange(3)} className="text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-700">
                {t('drivers.last_3_months', 'Last 3 months')}
              </button>
              <button type="button" onClick={() => setQuickRange(12)} className="text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-700">
                {t('drivers.last_year', 'Last year')}
              </button>
            </div>
          </div>

          {/* Show */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              {t('drivers.statement_show', 'Include')}
            </label>
            <div className="flex gap-2">
              {[
                { v: 'all', label: t('drivers.statement_all', 'All') },
                { v: 'trips', label: t('drivers.statement_trips_only', 'Trips only') },
                { v: 'payments', label: t('drivers.statement_payments_only', 'Payments only') },
              ].map(opt => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setEntryFilter(opt.v)}
                  className={`flex-1 text-sm px-3 py-2 rounded border ${
                    entryFilter === opt.v
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Language + Format */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {t('drivers.statement_language', 'Language')}
              </label>
              <div className="flex gap-2">
                {[
                  { v: 'en', label: 'English' },
                  { v: 'ar', label: 'العربية' },
                ].map(opt => (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => setLanguage(opt.v)}
                    className={`flex-1 text-sm px-3 py-2 rounded border ${
                      language === opt.v
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {t('drivers.statement_format', 'Format')}
              </label>
              <div className="flex gap-2">
                {[
                  { v: 'pdf', label: 'PDF' },
                  { v: 'excel', label: 'Excel' },
                ].map(opt => (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => setFormat(opt.v)}
                    className={`flex-1 text-sm px-3 py-2 rounded border ${
                      format === opt.v
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Preview totals */}
          <div className="bg-gray-50 rounded border border-gray-200 px-3 py-2 grid grid-cols-3 text-center">
            <div>
              <p className="text-[10px] uppercase text-gray-500">{t('drivers.total_trip_amount', 'Total trip amount')}</p>
              <p className="text-sm font-bold text-gray-900">{language === 'ar' ? 'ر.س' : 'SAR'} {filtered.totals.totalEarned.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase text-gray-500">{t('drivers.total_paid', 'Total paid')}</p>
              <p className="text-sm font-bold text-green-600">{language === 'ar' ? 'ر.س' : 'SAR'} {filtered.totals.totalPaid.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase text-gray-500">{t('drivers.remaining_balance', 'Balance')}</p>
              <p className={`text-sm font-bold ${filtered.totals.balance > 0 ? 'text-red-600' : filtered.totals.balance < 0 ? 'text-blue-600' : 'text-gray-900'}`}>
                {language === 'ar' ? 'ر.س' : 'SAR'} {filtered.totals.balance.toFixed(2)}
              </p>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-3">
          <button
            onClick={onClose}
            className="btn btn-ghost"
            disabled={generating}
          >
            {t('common.cancel', 'Cancel')}
          </button>
          <button
            onClick={handleGenerate}
            className="btn-primary flex items-center gap-2"
            disabled={generating}
          >
            <DocumentArrowDownIcon className="h-4 w-4" />
            {generating
              ? t('drivers.generating', 'Generating...')
              : t('drivers.download_statement', 'Download Statement')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DriverStatementModal;
